"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Crown, Eye, Sparkles } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function GoldenDawnMajorArcanaPage() {
  const majorArcanaCards = [
    { number: "0", name: "The Fool", hebrew: "Aleph", path: "Air", description: "Saf potansiyel ve ruhsal yolculuk", path_url: "the-fool" },
    { number: "I", name: "The Magician", hebrew: "Beth", path: "Mercury", description: "İrade gücü ve manifestasyon", path_url: "the-magician" },
    { number: "II", name: "The High Priestess", hebrew: "Gimel", path: "Moon", description: "Gizli bilgi ve sezgi", path_url: "the-high-priestess" },
    { number: "III", name: "The Empress", hebrew: "Daleth", path: "Venus", description: "Yaratıcılık ve bereket", path_url: "the-empress" },
    { number: "IV", name: "The Emperor", hebrew: "Heh", path: "Aries", description: "Otorite ve yapı", path_url: "the-emperor" },
    { number: "V", name: "The Hierophant", hebrew: "Vav", path: "Taurus", description: "Ruhsal öğretim", path_url: "the-hierophant" },
    { number: "VI", name: "The Lovers", hebrew: "Zayin", path: "Gemini", description: "Seçim ve birleşme", path_url: "the-lovers" },
    { number: "VII", name: "The Chariot", hebrew: "Cheth", path: "Cancer", description: "Zafer ve kontrol", path_url: "the-chariot" },
    { number: "VIII", name: "Strength", hebrew: "Teth", path: "Leo", description: "İç güç ve cesaret", path_url: "strength" },
    { number: "IX", name: "The Hermit", hebrew: "Yod", path: "Virgo", description: "İç arayış ve bilgelik", path_url: "the-hermit" },
    { number: "X", name: "Wheel of Fortune", hebrew: "Kaph", path: "Jupiter", description: "Kader döngüleri", path_url: "wheel-of-fortune" },
    { number: "XI", name: "Justice", hebrew: "Lamed", path: "Libra", description: "Denge ve adalet", path_url: "justice" },
    { number: "XII", name: "The Hanged Man", hebrew: "Mem", path: "Water", description: "Fedakarlık ve yeni bakış", path_url: "the-hanged-man" },
    { number: "XIII", name: "Death", hebrew: "Nun", path: "Scorpio", description: "Dönüşüm ve yeniden doğuş", path_url: "death" },
    { number: "XIV", name: "Temperance", hebrew: "Samekh", path: "Sagittarius", description: "Denge ve uyum", path_url: "temperance" },
    { number: "XV", name: "The Devil", hebrew: "Ayin", path: "Capricorn", description: "Maddi kısıtlamalar", path_url: "the-devil" },
    { number: "XVI", name: "The Tower", hebrew: "Peh", path: "Mars", description: "Ani değişim", path_url: "the-tower" },
    { number: "XVII", name: "The Star", hebrew: "Tzaddi", path: "Aquarius", description: "Umut ve ilham", path_url: "the-star" },
    { number: "XVIII", name: "The Moon", hebrew: "Qoph", path: "Pisces", description: "İllüzyon ve bilinçsiz", path_url: "the-moon" },
    { number: "XIX", name: "The Sun", hebrew: "Resh", path: "Sun", description: "Aydınlanma ve başarı", path_url: "the-sun" },
    { number: "XX", name: "Judgement", hebrew: "Shin", path: "Fire", description: "Yeniden doğuş", path_url: "judgement" },
    { number: "XXI", name: "The World", hebrew: "Tav", path: "Saturn", description: "Tamamlanma ve birlik", path_url: "the-world" }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-amber-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-amber-500">/</span>
            <span className="text-sm">Golden Dawn</span>
            <span className="text-amber-500">/</span>
            <span className="text-sm">Majör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/golden-dawn"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Golden Dawn Tarot
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Majör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 rounded-xl p-8 mb-8 border border-amber-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Crown className="h-10 w-10 text-amber-400" />
            Golden Dawn Majör Arkana
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            Hermetic Order of the Golden Dawn'un ezoterik öğretilerine dayanan 22 kart.
            Tree of Life'ın 22 yoluna karşılık gelen ve İbranice harflerle astrolojik bağlantıları olan sistem.
          </p>
        </div>

        {/* Tree of Life System */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tree of Life Correspondence Sistemi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-400" />
                Kabalistik Bağlantılar
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Her kart Tree of Life'da bir yola karşılık gelir</li>
                <li>• 22 İbranice harf ile bağlantılı</li>
                <li>• Sephirot'lar arasında köprü kurar</li>
                <li>• Yollar ruhsal gelişim aşamalarını gösterir</li>
                <li>• Meditasyon ve çalışma için yol haritası</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Eye className="h-5 w-5 text-yellow-400" />
                Astrolojik Sistemi
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Planetary ve zodiac etkiler</li>
                <li>• 4 element (Ateş, Su, Hava, Toprak)</li>
                <li>• 7 klasik planet</li>
                <li>• 12 zodiac işareti</li>
                <li>• Her kartın belirli astrolojik correspondence'ı</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">22 Majör Arkana Kartı</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {majorArcanaCards.map((card, index) => (
              <Link
                key={index}
                href={`/about/tarot/systems/golden-dawn/major-arcana/${card.path_url}`}
                className="group bg-slate-800 rounded-xl p-4 border border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/10"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-32 mb-3 rounded-lg overflow-hidden bg-gradient-to-b from-amber-900/20 to-slate-900 border border-amber-600/20 group-hover:scale-105 transition-transform duration-300">
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={`/images/tarot/cards/golden-dawn-${card.path_url}.svg`}
                        alt={card.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          if (target.nextElementSibling) {
                            (target.nextElementSibling as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                      <div className="hidden w-full h-full flex-col items-center justify-center text-amber-300">
                        <span className="text-lg font-bold">{card.number}</span>
                        <span className="text-xs text-center px-1">{card.name}</span>
                        <span className="text-xs mt-1">{card.hebrew}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="text-lg font-semibold text-slate-100 mb-1">{card.name}</div>
                    <div className="text-sm text-amber-400 mb-1">({card.number}) {card.hebrew}</div>
                    <div className="text-xs text-yellow-600 mb-1">{card.path}</div>
                    <div className="text-xs text-slate-400">{card.description}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Golden Dawn System */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Golden Dawn Tarot Sistemi</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Hermetic Öğretiler</h3>
              <p className="text-slate-300 text-sm mb-3">
                Antik Mısır, Yunan ve İbranice geleneklerin sentezi.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Hermes Trismegistus öğretileri</li>
                <li>• Kabala ve Tree of Life</li>
                <li>• Astroloji ve alchemy</li>
                <li>• Antik gizem okulları</li>
              </ul>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Practitioner System</h3>
              <p className="text-slate-300 text-sm mb-3">
                Ruhsal gelişim için pratik araçlar.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Meditasyon teknikleri</li>
                <li>• Pathworking yöntemleri</li>
                <li>• Ritual çalışmaları</li>
                <li>• Sembolik yorumlama</li>
              </ul>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Modern Etki</h3>
              <p className="text-slate-300 text-sm mb-3">
                Çağdaş tarot sistemlerinin temeli.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Rider-Waite sistemi</li>
                <li>• Thoth Tarot</li>
                <li>• Çoğu modern tarot destesi</li>
                <li>• Ezoterik correspondence'lar</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları Golden Dawn tarot sisteminin geleneksel yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/golden-dawn"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Golden Dawn Tarot
          </Link>

          <Link
            href="/about/tarot/systems/golden-dawn/minor-arcana"
            className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
          >
            Minör Arkana
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}